import { useLinkLogic } from '@/app/navigation/stack-links';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import {
  SignaturePad,
  SignaturePadRef,
} from '@/components/ui/SignaturePad/SignaturePad';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useSignWorkorderMutation,
  useWorkorder,
  useWorkorderLabor,
  useWorkorderLock,
  useWorkorderMaterials,
} from '@/features/workorders/workorders.hooks';
import { entityConfigs } from '@/lib/entities/entityConfig';
import { cn } from '@/lib/utils';
import { knownDepartments } from '@certhon/domain-models';
import { WorkorderLabor } from '@certhon/models';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Car,
  CheckCircle,
  Download,
  FileText,
  Lock,
  MapPin,
  Package,
  PenLine,
  Receipt,
  Clock,
  RotateCcw,
  User,
  Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { StackLink } from '@/app/navigation/StackLink';
import { useConfirm } from '@/components/ui/use-confirm';
import {
  formatDate,
  formatHours,
  formatTime,
  getSettlementName,
} from './workorders.utils';

interface SummaryItemProps {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  className?: string;
}

const SummaryItem = ({
  icon: Icon,
  label,
  value,
  className,
}: SummaryItemProps) => {
  if (!value) return null;
  return (
    <div className={cn('flex items-start gap-2', className)}>
      <Icon className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  );
};

function calculateLaborHours(entry: WorkorderLabor): number {
  const start = new Date(entry.date_start).getTime();
  const stop = new Date(entry.date_stop).getTime();
  return Math.max(0, (stop - start) / (1000 * 60 * 60));
}

function getDepartmentName(code: string | null): string | null {
  if (!code) return null;
  const dept = knownDepartments.find((d) => d.char === code);
  return dept?.name ?? code;
}

export const SignaturePanel = ({
  params,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const { workorderId } = params;
  const recnum = parseInt(workorderId);
  const makeLink = useLinkLogic();
  // This panel is a sibling of the details panel (…/:workorderId/sign), so the
  // details path is our own base path minus the trailing segment.
  const detailsPath = basePath.replace(/\/sign$/, '');

  const workorderQuery = useWorkorder(recnum);
  const laborQuery = useWorkorderLabor(recnum);
  const materialsQuery = useWorkorderMaterials(recnum);
  const signMutation = useSignWorkorderMutation(recnum);

  const workorder = workorderQuery.data;
  const labor = laborQuery.data ?? [];
  const materials = materialsQuery.data ?? [];

  const signaturePadRef = useRef<SignaturePadRef>(null);
  const [email, setEmail] = useState<string>('');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  // Entity lookups for technician and work
  const [technicianName, setTechnicianName] = useState<string | null>(null);
  const [workName, setWorkName] = useState<string | null>(null);

  // Fetch technician name from assigned_user_id
  useEffect(() => {
    if (workorder?.assigned_user_id) {
      entityConfigs.user
        .getById(String(workorder.assigned_user_id))
        .then((user) => setTechnicianName(user.name || user.loginname || null))
        .catch(() => setTechnicianName(null));
    }
  }, [workorder?.assigned_user_id]);

  // Fetch work name from werk_id
  useEffect(() => {
    if (workorder?.werk_id) {
      entityConfigs.work
        .getById(String(workorder.werk_id))
        .then((work) => setWorkName(work.oms || work.id || null))
        .catch(() => setWorkName(null));
    }
  }, [workorder?.werk_id]);

  // Initialize email from workorder when data loads
  // Prefer saved email, fall back to customer email
  useEffect(() => {
    if (!email && workorder) {
      const prefillEmail = workorder.email || workorder.customer_email;
      if (prefillEmail) {
        setEmail(prefillEmail);
      }
    }
  }, [workorder, email]);

  // Calculate totals
  const totalLaborHours = useMemo(() => {
    return labor.reduce(
      (sum, entry) => sum + calculateLaborHours(entry) - (entry.break ?? 0),
      0,
    );
  }, [labor]);

  const totalMaterials = materials
    .filter((m) => m.locked === false)
    .filter((m) => (m.quantity ?? 0) > 0).length;

  const hasDayfee = labor.some((l) => l.dayfee);

  const isCompleted = workorder?.completed ?? false;
  // Frozen when completed, concept, cancelled or assigned to someone else
  const { locked: isLocked, reason: lockReason } = useWorkorderLock(workorder);

  const handleConfirm = async () => {
    if (!workorder) return;

    // Validate signature
    const signature = signatureData ?? signaturePadRef.current?.toDataURL();
    if (!signature || signaturePadRef.current?.isEmpty()) {
      toast.error('Please provide a signature');
      return;
    }

    // Validate email
    if (!email) {
      toast.error('Please provide an email address');
      return;
    }

    const ok = await confirm({
      title: 'Complete workorder?',
      description: 'This cannot be undone. The customer will receive an email.',
      confirmLabel: 'Complete',
      destructive: true,
    });
    if (!ok) return;

    setIsSubmitting(true);
    try {
      await signMutation.mutateAsync({
        signature_data: signature,
        email: email,
      });
      // Toast is handled by the mutation's onSuccess
    } catch (error) {
      // Error toast is handled by the mutation's onError
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = workorder
    ? `W${workorder.id}: ${workorder.customer_name}`
    : `W${workorderId}`;

  if (workorderQuery.isLoading) {
    return (
      <Panel {...panelProps} title={title}>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      </Panel>
    );
  }

  if (!workorder) {
    return (
      <Panel {...panelProps} title={title}>
        <div className="text-muted-foreground text-center">
          Workorder not found
        </div>
      </Panel>
    );
  }

  // Build work location display (like legacy "Werk" section)
  const workLocation = [workName, workorder.location_address]
    .filter(Boolean)
    .join('\n');

  return (
    <Panel
      {...panelProps}
      title={title}
      headerActions={
        isCompleted && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              window.open(`/api/workorders/${workorder.recnum}/pdf`, '_blank')
            }
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        )
      }
    >
      <ConfirmDialog />
      <div className="space-y-6">
        {/* Summary Section */}
        <section>
          <h3 className="mb-3 text-sm font-semibold">Summary</h3>
          <div className="bg-muted/30 grid grid-cols-2 gap-3 rounded-lg border p-4">
            <SummaryItem
              icon={Building2}
              label="Customer"
              value={workorder.customer_name}
            />
            <SummaryItem
              icon={User}
              label="Contact"
              value={workorder.contact_name}
            />
            <SummaryItem
              icon={MapPin}
              label="Work"
              value={
                workLocation ? (
                  <span className="whitespace-pre-line">{workLocation}</span>
                ) : null
              }
            />
            <SummaryItem
              icon={Wrench}
              label="Technician"
              value={technicianName}
            />
            <SummaryItem
              icon={Receipt}
              label="Settlement"
              value={getSettlementName(workorder.settlement)}
            />
            <SummaryItem
              icon={Briefcase}
              label="Department"
              value={getDepartmentName(workorder.department)}
            />
            <SummaryItem
              icon={Clock}
              label="Labor"
              value={labor.length > 0 ? formatHours(totalLaborHours) : 'None'}
            />
            <SummaryItem
              icon={Package}
              label="Materials"
              value={totalMaterials > 0 ? `${totalMaterials} items` : 'None'}
            />

            {/* Description - full width */}
            {workorder.description && (
              <SummaryItem
                icon={FileText}
                label="Description"
                value={
                  <span className="whitespace-pre-wrap">
                    {workorder.description.split('--==')[0]}
                  </span>
                }
                className="col-span-2"
              />
            )}

            {/* Report - full width */}
            {workorder.report && (
              <SummaryItem
                icon={FileText}
                label="Work Completed"
                value={
                  <span className="whitespace-pre-wrap">
                    {workorder.report}
                  </span>
                }
                className="col-span-2"
              />
            )}
          </div>

          {/* Follow-up indicator */}
          {workorder.followup && (
            <p className="mt-3 flex items-center gap-2 text-sm">
              <RotateCcw className="h-4 w-4" />
              Follow-up is required
            </p>
          )}
        </section>

        {/* Materials Table */}
        {totalMaterials > 0 && (
          <section>
            <h3 className="mb-3 text-sm font-semibold">Materials</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24 text-right">Quantity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials
                  .filter((m) => (m.quantity ?? 0) > 0)
                  .filter((m) => m.locked === false)
                  .map((material) => (
                    <TableRow key={material.recnum}>
                      <TableCell>{material.text || '—'}</TableCell>
                      <TableCell className="text-right">
                        {material.quantity} {material.unit}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </section>
        )}

        {/* Labor Table */}
        {labor.length > 0 && (
          <section>
            <h3 className="mb-3 text-sm font-semibold">Labor</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="text-right">Break</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  {hasDayfee && (
                    <TableHead className="text-right">Day Fee</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {labor.map((entry) => {
                  const hours = calculateLaborHours(entry) - (entry.break ?? 0);
                  const breakMinutes = (entry.break ?? 0) * 60;
                  return (
                    <TableRow key={entry.recnum}>
                      <TableCell>{formatDate(entry.date_start)}</TableCell>
                      <TableCell>
                        {formatTime(entry.date_start)} -{' '}
                        {formatTime(entry.date_stop)}
                      </TableCell>
                      <TableCell className="text-right">
                        {breakMinutes > 0 ? `${breakMinutes}m` : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatHours(hours)}
                      </TableCell>
                      {hasDayfee && (
                        <TableCell className="text-right">
                          {entry.dayfee ? '✓' : ''}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} className="font-medium">
                    Total
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatHours(totalLaborHours)}
                  </TableCell>
                  {hasDayfee && (
                    <TableCell className="text-right font-medium">
                      {labor.filter((l) => l.dayfee).length}
                    </TableCell>
                  )}
                </TableRow>
              </TableFooter>
            </Table>
            {workorder.travelfee && (
              <p className="mt-2 flex items-center gap-2 text-sm">
                <Car className="h-4 w-4" />
                Travel fee applies
              </p>
            )}
          </section>
        )}

        {/* Terms & Conditions - small print, two languages */}
        <section className="text-muted-foreground space-y-3 text-[10px] leading-tight">
          <div>
            <p className="mb-1 font-medium">Voorwaarden</p>
            <p>
              Op de door Certhon Build B.V. uit te voeren werkzaamheden zijn de
              AVAG-voorwaarden van toepassing. Deze algemene voorwaarden kunnen
              worden geraadpleegd via{' '}
              <a
                href="https://www.avag.nl/nl/over-avag/verkoopvoorwaarden"
                className="underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.avag.nl/nl/over-avag/verkoopvoorwaarden
              </a>{' '}
              of worden op uw verzoek kosteloos naar u toegezonden. De algemene
              voorwaarden zijn beschikbaar in het Nederlands, Engels en Duits.
              Tijdens het uitvoeren van de beschreven werkzaamheden, kan de
              situatie zich voordoen dat de werknemer van Certhon Build B.V.
              bepaalde instellingen van de klimaatcomputer en/of de positie van
              één of meer schakelaars wijzigt/moet wijzigen. Ondanks de
              zorgvuldigheid die Certhon Build B.V. en haar werknemers bij de
              uitvoering van onze werkzaamheden betrachten, blijft de klant
              verantwoordelijk voor (de verificatie van) de juiste instellingen
              van de klimaatcomputer en/of de juiste posities van de schakelaars
              na afronding van deze werkzaamheden door Certhon Build B.V.
              Certhon Build B.V. is niet aansprakelijk voor enige schade die
              voortvloeit uit het feit dat bepaalde instellingen en/of
              schakelaars na afronding van de werkzaamheden niet op de juiste
              manier door de klant zijn gecorrigeerd.
            </p>
          </div>
          <div>
            <p className="mb-1 font-medium">Conditions</p>
            <p>
              The services to be provided by Certhon Build B.V. are subject to
              the AVAG terms and conditions. These general terms and conditions
              can be accessed via{' '}
              <a
                href="https://www.avag.nl/nl/over-avag/verkoopvoorwaarden"
                className="underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.avag.nl/nl/over-avag/verkoopvoorwaarden
              </a>{' '}
              or will be sent to you free of charge at your request. These
              general terms and conditions are available in Dutch, English and
              German. During the performance of the services as described above,
              the situation may arise that an employee of Certhon Build B.V.
              changes/needs to change certain settings of the climate computer
              and/or the position of one or more switches. Despite the utmost
              care taken by Certhon Build B.V. and its employees during the
              performance of our services, the customer remains responsible for
              the (verification of) the correct settings of the climate computer
              and/or the correct position of the switches after completion of
              the services performed by Certhon Build B.V. Certhon Build B.V. is
              not liable for any damage resulting from certain settings and/or
              switches not having been checked or corrected by the customer
              after completion of the services.
            </p>
          </div>
          <p className="pt-2">
            Certhon Build B.V. | K.v.K 27237620 | BTW-nr NL8033.18.613.B.01
          </p>
        </section>

        <Separator />

        {/* Email Input */}
        <section>
          <Label htmlFor="email" className="text-sm font-semibold">
            Email for Report
          </Label>
          <p className="text-muted-foreground mb-2 text-xs">
            A copy of this workorder will be sent to this email address.
          </p>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="customer@example.com"
            disabled={isLocked}
            className="max-w-md"
          />
        </section>

        <Separator />

        {/* Signature Section */}
        <section>
          <h3 className="mb-3 text-sm font-semibold">Signature</h3>
          {isCompleted ? (
            // View mode: show signature
            <div className="space-y-4">
              {workorder.signature_data ? (
                <div className="inline-block overflow-hidden rounded-md border bg-white p-2">
                  <img
                    src={workorder.signature_data}
                    alt="Signature"
                    className="h-auto max-h-48 max-w-full"
                  />
                </div>
              ) : (
                <div className="text-muted-foreground flex items-center gap-2">
                  <PenLine className="h-4 w-4" />
                  <span>No signature</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>
                  {workorder.signature_data
                    ? 'Signed and completed'
                    : 'Completed'}
                  {workorder.date_completed &&
                    ` on ${formatDate(new Date(workorder.date_completed))} at ${formatTime(new Date(workorder.date_completed))}`}
                </span>
              </div>
            </div>
          ) : isLocked ? (
            // Open but frozen for this user: no signing possible
            <div className="text-muted-foreground flex items-start gap-2 text-sm">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{lockReason}</span>
            </div>
          ) : (
            // Edit mode: show signature pad
            <div className="space-y-4">
              <SignaturePad
                ref={signaturePadRef}
                width={400}
                height={200}
                onChange={(dataUrl) => setSignatureData(dataUrl)}
              />
              <Button
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                {isSubmitting ? (
                  'Signing...'
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Confirm & Complete
                  </>
                )}
              </Button>
            </div>
          )}
        </section>

        <Separator />

        {/* Back to Workorder Details Button */}
        <section>
          <Button variant="outline" asChild tooltip={false}>
            <StackLink to={detailsPath}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Workorder Details
            </StackLink>
          </Button>
        </section>
      </div>
    </Panel>
  );
};
